import { useEffect, useRef, useState } from 'react'

import citricAcidImg from '~/widgets/CostBomDemo/assets/images/citric-acid.png'
import nmcBatteryImg from '~/widgets/CostBomDemo/assets/images/nmc-battery.png'
import smartHomeSocImg from '~/widgets/CostBomDemo/assets/images/smart-home-soc.png'
import { HeroConversionModal } from '~/widgets/CostBomDemo/components/HeroConversionModal'
import { HeroHead } from '~/widgets/CostBomDemo/components/HeroHead'
import { HeroProductDropdown } from '~/widgets/CostBomDemo/components/HeroProductDropdown'
import { resetLegacyEngine } from '~/widgets/CostBomDemo/engine/runLegacyEngine'
import { useCostModelDemo } from '~/widgets/CostBomDemo/hooks/useCostModelDemo'
import { useCostModelLead } from '~/widgets/CostBomDemo/hooks/useCostModelLead'
import {
  COST_BOM_DEMO_STRINGS,
  SAMPLE_PRODUCTS,
} from '~/widgets/CostBomDemo/strings'
import '~/widgets/CostBomDemo/styles/bomScoped.css'
import '~/widgets/CostBomDemo/styles/breakdown.css'
import '~/widgets/CostBomDemo/styles/cm2.css'
import '~/widgets/CostBomDemo/styles/conversionModal.css'
import '~/widgets/CostBomDemo/styles/costBomDemo.css'
import '~/widgets/CostBomDemo/styles/heroFigma.css'
import '~/widgets/CostBomDemo/styles/tabs.css'

const PRODUCT_IMAGES: Record<string, string> = {
  'NMC Battery Cell': nmcBatteryImg,
  'Citric Acid': citricAcidImg,
  'Smart Home SoC': smartHomeSocImg,
}

// Auto-open the lead form this long after a model finishes generating.
const AUTO_LEAD_DELAY_MS = 4500

export function CostBomDemoInner() {
  const rootRef = useRef<HTMLElement>(null)
  const { submitLead } = useCostModelLead()
  const [conversionOpen, setConversionOpen] = useState(false)
  // Mirror of conversionOpen for the timer callback, so it can skip re-opening
  // when the form is already showing.
  const conversionOpenRef = useRef(false)
  conversionOpenRef.current = conversionOpen

  useCostModelDemo({
    rootRef,
    productImages: PRODUCT_IMAGES,
    onLead: submitLead,
  })

  // The legacy engine (cm2Engine.js) and the hero dropdown open the conversion
  // form by calling this global. React owns the modal state; this is the seam.
  useEffect(() => {
    window.openHeroConversionModal = () => setConversionOpen(true)
    return () => {
      delete window.openHeroConversionModal
    }
  }, [])

  // Auto-open the lead form a few seconds after EVERY generation settles.
  // Fires on each transition to the `ready` phase (initial generate, tab
  // switches, added samples). Skips if the form is already open, and a pending
  // pop is cancelled if a new generation starts before it fires.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return () => {}

    let timer: number | undefined
    const onPhaseChange = () => {
      if (timer != null) {
        window.clearTimeout(timer)
        timer = undefined
      }
      if (root.getAttribute('data-hero-phase') !== 'ready') return
      timer = window.setTimeout(() => {
        if (!conversionOpenRef.current) setConversionOpen(true)
      }, AUTO_LEAD_DELAY_MS)
    }

    const observer = new MutationObserver(onPhaseChange)
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-hero-phase'],
    })
    onPhaseChange() // in case a model is already settled

    return () => {
      observer.disconnect()
      if (timer != null) window.clearTimeout(timer)
    }
  }, [rootRef])

  useEffect(() => () => resetLegacyEngine(), [])

  return (
    <div className="costBomDemo">
      <section
        ref={rootRef}
        className="mi-hero-section solution cost_model hero-figma"
        id="hero"
        data-hero-phase="idle"
      >
        <div className="hero-frame">
          <div className="hero-shell">
            <HeroHead />

            <div
              className="hero-browser cm2-inline-browser mi-window"
              id="cm2-inline-browser"
            >
              <div className="mi-window__bar hero-browser__bar">
                <span className="mi-window__dots hero-browser__dots">
                  <i />
                  <i />
                  <i />
                </span>
              </div>

              <div className="hero-building-card">
                <div className="hero-form-row">
                  <div className="hero-form-fields">
                    <div className="hero-form-group">
                      <span className="hero-lbl">
                        {COST_BOM_DEMO_STRINGS.buildingLabel}
                      </span>
                      <HeroProductDropdown />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="hero-generate"
                    id="hero-generate-btn"
                  >
                    {COST_BOM_DEMO_STRINGS.generate}
                  </button>
                </div>
                <div
                  className="hero-gen-status"
                  id="hero-gen-status"
                  aria-live="polite"
                >
                  <span className="hero-gen-status-dot" aria-hidden="true" />
                  <span
                    className="hero-gen-status-txt"
                    id="hero-gen-status-txt"
                  >
                    Understanding your product…
                  </span>
                </div>
                <div className="hero-product-clip" aria-hidden="true">
                  <img
                    className="hero-product-visual is-shown"
                    id="hero-product-visual"
                    src={nmcBatteryImg}
                    alt=""
                    aria-hidden="true"
                    data-product="NMC Battery Cell"
                  />
                </div>
              </div>

              <div
                className="mi-window__body cm2-panels hero-browser__body"
                id="cm2-panels"
              >
                <div id="hero-sample-root" />
              </div>

              <div
                className="mi-bd"
                id="mi-bd"
                role="dialog"
                aria-label="Cost breakdown"
              />

              <div
                className="mi-window__tabs cm2-tabs"
                id="cm2-tabs"
                role="tablist"
                hidden
              >
                <div className="cm2-tabs__strip" id="cm2-tabs-strip">
                  <div
                    className="hero-tab-add-wrap hero-dd"
                    id="hero-tab-add-wrap"
                    hidden
                  >
                    <button
                      type="button"
                      className="cm2-tab-add"
                      id="hero-tab-add"
                      aria-haspopup="listbox"
                      aria-expanded="false"
                      aria-label="Add sample"
                    >
                      <svg viewBox="0 0 13 13" fill="none" aria-hidden="true">
                        <path
                          d="M6.5 1v11M1 6.5h11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                    <div
                      className="hero-dd-pop hero-tab-add-pop"
                      id="hero-tab-add-pop"
                      hidden
                      role="listbox"
                    >
                      <div className="hero-tab-add-pop-hd">
                        {COST_BOM_DEMO_STRINGS.addSample}
                      </div>
                      {SAMPLE_PRODUCTS.map((sample, idx) => (
                        <div key={sample.key}>
                          {idx > 0 && (
                            <div
                              className="hero-dd-divider"
                              role="separator"
                              aria-hidden="true"
                            />
                          )}
                          <button
                            type="button"
                            className="hero-dd-opt"
                            data-product={sample.product}
                            data-country={sample.country}
                            data-key={sample.key}
                          >
                            <span className="hero-dd-opt-name">
                              {sample.product}
                            </span>
                            <span className="hero-dd-opt-mid">
                              {COST_BOM_DEMO_STRINGS.sourcingFrom}
                            </span>
                            <span className="hero-dd-opt-country">
                              {sample.country}
                            </span>
                          </button>
                        </div>
                      ))}
                      <div
                        className="hero-dd-divider"
                        role="separator"
                        aria-hidden="true"
                      />
                      <button
                        type="button"
                        className="hero-dd-opt hero-dd-opt--custom"
                        data-custom="true"
                      >
                        <span className="hero-dd-opt-custom">
                          <span className="hero-dd-opt-name">
                            {COST_BOM_DEMO_STRINGS.yourOwnProduct}
                          </span>
                          <span className="hero-dd-opt-hint">
                            {COST_BOM_DEMO_STRINGS.buildModelHint}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  className="hero-tab-cta-wrap"
                  id="hero-tab-cta-wrap"
                  hidden
                >
                  <button
                    type="button"
                    className="hero-tab-cta"
                    id="build-cost-model"
                  >
                    <span>{COST_BOM_DEMO_STRINGS.buildYourOwn}</span>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M5 12h14M13 6l6 6-6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HeroConversionModal
        opened={conversionOpen}
        onClose={() => setConversionOpen(false)}
        submitLead={submitLead}
      />
    </div>
  )
}
